import { Constants, TITLE_TRACKERS } from "@eagerdog/constants";

import { IDogRun } from "../title-tracker/dog-run.interface";
import { IPostRunSummary } from "./post.interface";

/** "AKC Scent Work" etc. — the same label the Ribbon Room sport cards use. */
export const runSportLabel = (run: Pick<IPostRunSummary, "show_type" | "sanctioning_club">): string => {
  const tracker = Object.values(TITLE_TRACKERS).find(
    (t) => t.show_type === run.show_type && t.sanctioning_club === run.sanctioning_club
  );
  return tracker?.label || [run.sanctioning_club, run.show_type].filter(Boolean).join(" ");
};

const RUN_RESULT_LABEL: Record<string, string> = {
  [Constants.run_result_status.qualified]: "Qualified",
  [Constants.run_result_status.not_qualified]: "Not Qualified",
  [Constants.run_result_status.absent]: "Absent",
  [Constants.run_result_status.disqualified]: "Disqualified",
  [Constants.run_result_status.excused]: "Excused",
  [Constants.run_result_status.scratched]: "Scratched",
};

const ordinal = (n: number): string => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const formatSeconds = (s: number): string => {
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const rest = s - m * 60;
    return `${m}:${rest.toFixed(2).padStart(5, "0")}`;
  }
  return `${s.toFixed(2)}s`;
};

const formatDate = (d?: Date | string): string | undefined => {
  if (!d) return undefined;
  const date = new Date(d);
  if (isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/** The feed card's run block as plain strings, for the card, share text, OG tags and email. */
export interface IRunSummaryText {
  /** "AKC Scent Work" */
  sport: string;
  /** "Excellent · Container · Section B · Division A" — whichever the sport recorded. */
  classLine?: string;
  /** Date, event name, show name, city/state — in that order, only those present. */
  whereParts: string[];
  /** "Qualified", "NQ", "Pass" … */
  result?: string;
  qualified: boolean;
  /** Show-type-specific stats, labelled: [["Position","1"], ["Search Time","11:11:11"], …] */
  stats: Array<[string, string]>;
}

/**
 * One rendering of a run summary, shared by the web RunSummary card, the
 * share description / OG tags and the result emails so they all say the
 * same thing. Add fields here, not in the consumers.
 */
export const runSummaryText = (run: IPostRunSummary): IRunSummaryText => {
  const resultCode = run.result_status || (run.qualified === true ? Constants.run_result_status.qualified : undefined);
  const qualified = run.qualified === true || resultCode === Constants.run_result_status.qualified;
  // Club-entered results carry a ready display label; logged runs carry a code.
  const result = run.result_label || (resultCode ? (RUN_RESULT_LABEL[resultCode] || resultCode) : undefined)
    || (run.qualified === false ? RUN_RESULT_LABEL[Constants.run_result_status.not_qualified] : undefined);

  const classParts = [
    run.class || run.level,
    run.element,
    run.section ? `Section ${run.section}` : null,
    run.division ? `Division ${run.division}` : null,
  ].filter(Boolean) as string[];

  const whereParts = [
    formatDate(run.trial_date),
    run.event_name,
    run.show_name,
    [run.city, run.state].filter(Boolean).join(", ") || null,
  ].filter(Boolean) as string[];

  // Club-entered results: the same show-type-specific stats the Results
  // grid shows, already labelled by scoring-core.
  const stats: Array<[string, string]> = (run.fields || []).map((f) => [f.label, f.value]);
  if (typeof run.score === "number") stats.push(["Score", String(run.score)]);
  if (typeof run.placement === "number" && run.placement > 0) stats.push(["Place", ordinal(run.placement)]);
  if (run.time) stats.push(["Time", run.time]);
  else if (typeof run.time_seconds === "number") stats.push(["Time", formatSeconds(run.time_seconds)]);
  if (typeof run.mph === "number") stats.push(["Speed", `${run.mph.toFixed(2)} mph`]);
  if (typeof run.points_earned === "number") stats.push(["Points", String(run.points_earned)]);
  if (typeof run.hides_found === "number") stats.push(["Hides", String(run.hides_found)]);
  if (typeof run.faults === "number") stats.push(["Faults", String(run.faults)]);

  return {
    sport: runSportLabel(run),
    classLine: classParts.join(" · ") || undefined,
    whereParts,
    result,
    qualified,
    stats,
  };
};

/**
 * Project a dog run onto the subset the feed card displays. Shared by the
 * API (when it snapshots the run onto the post) and the web composer (for
 * the preview shown before posting), so both render the same thing.
 */
export function toPostRunSummary(run: Partial<IDogRun>): IPostRunSummary {
  const summary: IPostRunSummary = {
    show_type: run.show_type || "",
    sanctioning_club: run.sanctioning_club || "",
    trial_date: run.trial_date,
    event_name: run.event_name || undefined,
    city: run.location?.city || undefined,
    state: run.location?.state || undefined,
    class: run.class || undefined,
    level: run.level || undefined,
    element: run.element || undefined,
    section: run.section || undefined,
    division: run.division || undefined,
    result_status: run.result_status || undefined,
    qualified: typeof run.qualified === "boolean" ? run.qualified : undefined,
    score: typeof run.score === "number" ? run.score : undefined,
    placement: typeof run.placement === "number" ? run.placement : undefined,
    time: run.time || undefined,
    time_seconds: typeof run.time_seconds === "number" ? run.time_seconds : undefined,
    mph: typeof run.mph === "number" ? run.mph : undefined,
    points_earned: typeof run.points_earned === "number" ? run.points_earned : undefined,
    faults: typeof run.faults === "number" ? run.faults : undefined,
    hides_found: typeof run.hides_found === "number" ? run.hides_found : undefined,
    awards: run.awards && run.awards.length > 0 ? run.awards : undefined,
  };

  // Drop undefined keys so the stored subdocument stays compact.
  (Object.keys(summary) as Array<keyof IPostRunSummary>).forEach((k) => {
    if (summary[k] === undefined) delete summary[k];
  });

  return summary;
}
